import { requestMissingChatsImagesThunk, handleChatsInitializationThunk, currentChatListRequest, isChatListRequestCurrent } from './redux/TeamInboxThunks';
import { normalizeChat } from './team-inbox-helpers';
import { waQueryContactsAndChatsTool } from '@common/tools/whatsapp';
import { TeamInboxChatsCacheReducerActions } from '@common/reducers/DBServiceThunks';
import { selectChatsSortedByTimestamp, selectNonArchivedChatsSortedByTimestamp, selectArchivedChatsSortedByTimestamp, selectIsArchivedView, selectChannelChatsSortedByTimestamp, selectIsChannelsView } from './redux/TeamInboxSelectors';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { useAppDispatch, useAppSelector, useDebounce } from '@common/reducers/hooks/store.hook';
import { selectUserId } from '@common/reducers/UserSelectors';
import { selectOnlineEngines } from '@common/reducers/ConnectedEnginesSlice';
import { Box, CircularProgress, debounce, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import ChatDisplayRow from './chat-display-row';
import ContactDisplayRow from './contact-display-row';
import EmptyState from './empty-state';
import GroupHeader from './group-header';
import classes from './team-inbox.module.scss';

// Constants
const SEARCH_DEBOUNCE_MS = 300;
const IMAGE_UPDATE_DEBOUNCE_MS = 250;
const MAX_CHAT_LOAD_RETRIES = 5;

type DisplayItem = {
    type: 'header' | 'chat' | 'contact' | 'noResults';
    data?: any;
    title?: string;
    count?: number;
    message?: string;
};

const WaChatLazyLoadingVirtuoso: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const chatsSidebarSearchToken = useAppSelector((state) => state.TeamInboxReducer?.chatsSidebarSearchToken);
    const debouncedSearchFilter = useDebounce(chatsSidebarSearchToken, SEARCH_DEBOUNCE_MS);

    // === STATE ===
    const userId = useAppSelector(selectUserId);
    const isArchivedView = useAppSelector(selectIsArchivedView);
    const isChannelsView = useAppSelector(selectIsChannelsView);
    const allChatsForSearch = useAppSelector(selectChatsSortedByTimestamp);
    const nonArchivedChats = useAppSelector(selectNonArchivedChatsSortedByTimestamp);
    const archivedChats = useAppSelector(selectArchivedChatsSortedByTimestamp);
    const channelChats = useAppSelector(selectChannelChatsSortedByTimestamp);

    // Use appropriate chat list based on view mode
    const allChats = isArchivedView ? archivedChats : isChannelsView ? channelChats : nonArchivedChats;
    const chatsLoading = useAppSelector(state => state.DBTeamInboxChatsCacheReducer.itemsLoading);
    const chatsLoadedLocally = useAppSelector(state => state.DBTeamInboxChatsCacheReducer.itemsLoadedLocaly);
    const hasMoreChatsToLoad = useAppSelector(state => state.TeamInboxReducer?.hasMoreChatsToLoad);
    const { searchResponseStatus, contacts } = useAppSelector(state =>
        state.TeamInboxReducer?.filteredChatAndContactList ?? { contacts: [], searchResponseStatus: 'noSearchValue' }
    );

    // Any online WhatsApp ENGINE (extension, Chrome gateway or Baileys pod) —
    // never "any MQTT client", which includes this website's own presence and
    // made the flag permanently true (the recovery effects below key off it).
    const hasWhatsAppConnection = useAppSelector(selectOnlineEngines).length > 0;

    // === LOCAL STATE ===
    const [offset, setOffset] = useState(0);
    const [previousSearchMode, setPreviousSearchMode] = useState(false);

    // Bounded retry budget for the empty-list recovery effect below
    const chatLoadRetryCount = useRef(0);

    // === DERIVED STATE ===
    const isSearchMode = debouncedSearchFilter.trim().length > 0;
    const hasSearchQuery = chatsSidebarSearchToken.trim().length > 0;
    const isSearchLoading = hasSearchQuery && searchResponseStatus !== 'received';
    const isLoading = (chatsLoading && !chatsLoadedLocally) || isSearchLoading;
    const isEmpty = chatsLoadedLocally && allChats.length === 0 && !hasMoreChatsToLoad && !isSearchMode;

    // Status text shown under the spinner so it's visible what the loader is
    // actually waiting on (search vs. WhatsApp connection vs. chat fetch/retry).
    const loaderStatus = isSearchLoading
        ? t('teamInbox.searchingChats', 'Searching chats…')
        : !hasWhatsAppConnection
            ? t('teamInbox.connectingToWhatsApp', 'Connecting to WhatsApp…')
            : chatLoadRetryCount.current > 0
                ? t('teamInbox.retryingChatsLoad', 'Loading chats from WhatsApp… (retry {{attempt}}/{{max}})', { attempt: chatLoadRetryCount.current, max: MAX_CHAT_LOAD_RETRIES })
                : t('teamInbox.loadingChats', 'Loading chats from WhatsApp…');

    // === FUNCTIONS ===


    const loadMoreChats = useCallback(() => {
        // Prevent loadMoreChats if we just exited search mode
        const justExitedSearchMode = previousSearchMode && !isSearchMode;
        const canLoadMore = hasMoreChatsToLoad && !chatsLoading && !isSearchMode && !justExitedSearchMode;

        if (canLoadMore) {
            // Snapshot the chat-list request id: if a search or full reload
            // starts while this page is in flight, the response is stale and
            // must not be upserted into the (now rebuilt) cache.
            const requestId = currentChatListRequest();
            dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(true));
            waQueryContactsAndChatsTool({
                includeContacts: false,
                includeChats: true,
                includeLastMessage: true,
                includeArchive: true, // keep archived chats in the paginated list (engine drops them by default)
                limit: 50,
                offset,
            }).then((result) => {
                if (!isChatListRequestCurrent(requestId)) {
                    console.log('[team-inbox] loadMoreChats: dropping stale response', { requestId });
                    return;
                }
                if (result.success === false) {
                    console.error('[team-inbox] loadMoreChats: tool returned failure', { error: result.error });
                    return;
                }
                const data = result.data as { chats?: any[] } | undefined;
                const chats = data?.chats || [];
                if (chats.length > 0) {
                    dispatch(TeamInboxChatsCacheReducerActions.upsertMany(chats.map(normalizeChat)));
                    dispatch(TeamInboxActions.setHasMoreChatsToLoad(chats.length >= 50));
                } else {
                    dispatch(TeamInboxActions.setHasMoreChatsToLoad(false));
                }
            }).catch((error) => {
                console.error('[team-inbox] loadMoreChats failed', { error });
            }).finally(() => {
                // Loading flags are owned by the latest request — don't clear
                // them out from under a superseding search/reload.
                if (isChatListRequestCurrent(requestId)) {
                    dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(false));
                    dispatch(TeamInboxChatsCacheReducerActions.setItemsLoadedLocaly(true));
                }
            });
            setOffset(prev => prev + 50);
        }
    }, [hasMoreChatsToLoad, chatsLoading, isSearchMode, previousSearchMode, offset, dispatch]);

    // Stable debounced fn: recreating `debounce(...)` every render (the previous behavior)
    // made a brand-new debouncer per render, so the timer never actually coalesced calls —
    // each render reset it. Memoized on `dispatch` (stable) so it debounces correctly, and
    // cancelled on unmount so no trailing dispatch fires after teardown.
    const debouncedRequestMissingChatsImages = useMemo(
        () => debounce((chatIds: string[]) => {
            dispatch(requestMissingChatsImagesThunk({ chatIds }));
        }, IMAGE_UPDATE_DEBOUNCE_MS),
        [dispatch]
    );

    useEffect(() => {
        return () => debouncedRequestMissingChatsImages.clear();
    }, [debouncedRequestMissingChatsImages]);

    // Virtuoso reports indices into the *rendered* displayItems list — resolve
    // them to the exact chat/contact ids on screen (groups included) so the
    // profile-pic prefetch targets what the user actually sees, not a slice of
    // a differently-ordered selector.
    const displayItemsRef = useRef<DisplayItem[]>([]);
    const handleRangeChanged = useCallback(({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
        const visible = displayItemsRef.current.slice(startIndex, endIndex + 1);
        const ids = visible
            .map(item => {
                if (item.type === 'chat') return item.data?.id?._serialized || item.data?._serialized;
                if (item.type === 'contact') return typeof item.data === 'string' ? item.data : (item.data?.id?._serialized || item.data?.chatId);
                return null;
            })
            .filter(Boolean) as string[];
        if (ids.length > 0) {
            debouncedRequestMissingChatsImages(ids);
        }
    }, [debouncedRequestMissingChatsImages]);

    // === DISPLAY LIST LOGIC ===
    const displayItems = useMemo((): DisplayItem[] => {
        const showSearchResults = isSearchMode && searchResponseStatus === 'received';

        if (showSearchResults) {
            const items: DisplayItem[] = [];

            // In search mode, use all chats (including archived) for search results
            // Chats section
            items.push({ type: 'header', title: t('teamInbox.chats'), count: allChatsForSearch.length });

            if (allChatsForSearch.length > 0) {
                allChatsForSearch.forEach(chat => {
                    items.push({ type: 'chat', data: chat });
                });
            } else {
                items.push({ type: 'noResults', message: t('teamInbox.noChatsFound') });
            }

            // Contacts section
            items.push({ type: 'header', title: t('teamInbox.contacts'), count: contacts.length });

            if (contacts.length > 0) {
                contacts.forEach(contact => {
                    items.push({ type: 'contact', data: contact });
                });
            } else {
                items.push({ type: 'noResults', message: 'No contacts found' });
            }

            return items;
        }

        // Archived + Channels live in the header now (ChatsSideBarFolders) —
        // the list shows only chats (inbox, or the active folder's chats).
        const items: DisplayItem[] = [];
        allChats.forEach(chat => {
            items.push({ type: 'chat', data: chat });
        });

        return items;
    }, [isSearchMode, allChats, allChatsForSearch, contacts, searchResponseStatus]);

    // Keep the ref current for handleRangeChanged (mutating a ref in render is
    // safe here — it's a read-through cache, not state that drives rendering).
    displayItemsRef.current = displayItems;

    // === RENDER FUNCTIONS ===
    const renderItem = useCallback((item: DisplayItem) => {
        switch (item.type) {
            case 'header':
                return <GroupHeader title={item.title!} count={item.count!} />;
            case 'chat':
                return <ChatDisplayRow itemId={item.data?._serialized} />;
            case 'contact':
                return <ContactDisplayRow contactId={item.data} />;
            case 'noResults':
                return <EmptyState description={item.message || 'No results found'} variant="small" />;
            default:
                return null;
        }
    }, []);

    // === EFFECTS ===
    // Picking a label view in the sidebar should always land on the regular
    // list — without this, switching views while browsing Archived leaves the
    // list stuck on the archived chats.
    const activeLabelId = useAppSelector(state => (state as any).LabelReducer?.activeLabelId ?? '');
    useEffect(() => {
        if (isArchivedView) {
            dispatch(TeamInboxActions.setIsArchivedView(false));
        }
        if (isChannelsView) {
            dispatch(TeamInboxActions.setIsChannelsView(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeLabelId]);

    useEffect(() => {
        // Wait for userId to be available before initializing chats
        if (!userId) {
            return;
        }

        const handleInitialization = async () => {
            await dispatch(handleChatsInitializationThunk({
                debouncedSearchFilter
            }));

            // Update offset when initial chats are loaded (when not in search mode)
            if (!isSearchMode) {
                setOffset(50);
            }
        };

        handleInitialization();

        // Update previous search mode after the effect runs
        setPreviousSearchMode(isSearchMode);
    }, [debouncedSearchFilter, dispatch, isSearchMode, userId]);

    // Retry loading chats while the list is empty. Covers two failure modes:
    // 1. The MQTT-presence race — initial load fired before the WhatsApp
    //    connection was registered (first retry fires as soon as it appears).
    // 2. The backend being briefly unreachable (dev-watch restarts, network
    //    blips) — the initial fetch fails with a network error and nothing
    //    would ever re-fetch, leaving a permanent "No chats found".
    // Bounded with backoff so a genuinely empty account doesn't poll forever.
    useEffect(() => {
        const shouldRetry = userId &&
            chatsLoadedLocally &&
            allChats.length === 0 &&
            !isSearchMode &&
            !chatsLoading &&
            chatLoadRetryCount.current < MAX_CHAT_LOAD_RETRIES;

        if (!shouldRetry) return;

        const attempt = chatLoadRetryCount.current + 1;
        // Immediate when the WhatsApp connection just became available
        // (the original race-condition case); otherwise back off.
        const delayMs = hasWhatsAppConnection && attempt === 1 ? 0 : Math.min(attempt * 4000, 15000);

        const timer = setTimeout(() => {
            chatLoadRetryCount.current = attempt;
            // Snapshot the chat-list request id so a search/reload that starts
            // while this retry is in flight invalidates its response.
            const requestId = currentChatListRequest();
            // Fetch ALL chats at once to ensure archived chats are included
            dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(true));
            waQueryContactsAndChatsTool({
                includeContacts: false,
                includeChats: true,
                includeLastMessage: true,
                includeArchive: true, // ensure archived chats are included in the recovery fetch
                limit: 10000,
                offset: 0,
            }).then((result) => {
                if (!isChatListRequestCurrent(requestId)) {
                    console.log('[team-inbox] retry chats load: dropping stale response', { attempt, requestId });
                    return;
                }
                if (result.success === false) {
                    console.error('[team-inbox] retry chats load: tool returned failure', { attempt, error: result.error });
                    return;
                }
                const data = result.data as { chats?: any[] } | undefined;
                const chats = data?.chats || [];
                if (chats.length > 0) {
                    dispatch(TeamInboxChatsCacheReducerActions.upsertMany(chats.map(normalizeChat)));
                    dispatch(TeamInboxActions.setHasMoreChatsToLoad(chats.length >= 10000));
                }
            }).catch((error) => {
                console.error('[team-inbox] retry chats load failed', { attempt, error });
            }).finally(() => {
                if (isChatListRequestCurrent(requestId)) {
                    dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(false));
                    dispatch(TeamInboxChatsCacheReducerActions.setItemsLoadedLocaly(true));
                }
            });
            setOffset(10000);
        }, delayMs);

        return () => clearTimeout(timer);
    }, [userId, hasWhatsAppConnection, chatsLoadedLocally, allChats.length, isSearchMode, chatsLoading, dispatch]);

    // Reset retry budget when user changes, OR when a WhatsApp engine
    // (re)connects. The latter is the recovery path: a WA-engine outage longer
    // than the bounded retry window (e.g. the WhatsApp Web tab reloading) burns
    // all MAX_CHAT_LOAD_RETRIES attempts against NO_WHATSAPP_CONNECTION, after
    // which nothing would re-fetch and the list stayed empty until a full page
    // reload. Refunding the budget on the absent→present edge lets the empty-list
    // effect re-fire and load chats the moment the engine comes back. The effect
    // only runs on the boolean transition, so a genuinely empty account still
    // stops after MAX_CHAT_LOAD_RETRIES (no infinite polling).
    useEffect(() => {
        chatLoadRetryCount.current = 0;
    }, [userId, hasWhatsAppConnection]);

    // === RENDER ===
    return (
        <Stack sx={{ height: "100%" }}>
            {isLoading && (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginTop: '20px'
                }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary">
                        {loaderStatus}
                    </Typography>
                </Box>
            )}
            {isEmpty && !isLoading && (
                <EmptyState
                    description={isArchivedView
                        ? t('teamInbox.noArchivedChatsFound')
                        : t('teamInbox.noChatsFound')
                    } />
            )}
            {!isLoading && !isEmpty && (
                <Virtuoso
                    rangeChanged={handleRangeChanged}
                    data={displayItems}
                    endReached={isSearchMode ? undefined : loadMoreChats}
                    className={classes.virtuosoScroll}
                    style={{ height: "100%", minWidth: "100%" }}
                    totalCount={displayItems.length}
                    increaseViewportBy={10}
                    components={{
                        Footer: () => (
                            hasMoreChatsToLoad && chatsLoading ? (
                                <Box className={classes.loadingMoreChatsWrapper}>
                                    <Stack className={classes.loadingMoreChatsStack}>
                                        <CircularProgress 
                                            size={14} 
                                            thickness={2}
                                            sx={{ color: 'primary.main' }}
                                        />
                                        <span style={{ fontSize: '14px' }}>
                                            {t('teamInbox.loadingMoreChats')}
                                        </span>
                                    </Stack>
                                </Box>
                            ) : (
                                <div style={{ height: '16px' }} />
                            )
                        )
                    }}
                    itemContent={(_, item) => renderItem(item)}
                />
            )}
        </Stack>
    );
};

export default React.memo(WaChatLazyLoadingVirtuoso); 